/**
 * Utility functions for phone number formatting and validation
 */

/**
 * Normalize a phone number to E.164 format (+919876543210)
 * @param {string} phoneNumber - Raw phone number in any format
 * @returns {string|null} - Normalized phone number or null if invalid
 */
export const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return null;
  }

  // Remove all non-digit characters
  let digitsOnly = phoneNumber.replace(/\D/g, '');

  // Handle empty result after cleaning
  if (!digitsOnly) {
    return null;
  }

  // Remove leading zeros
  digitsOnly = digitsOnly.replace(/^0+/, '');

  // Case 1: Already has country code (91XXXXXXXXXX - 12 digits)
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    const mobileNumber = digitsOnly.substring(2);
    if (isValidIndianMobile(mobileNumber)) {
      return '+91' + mobileNumber;
    }
  }

  // Case 2: 10-digit Indian mobile number
  if (digitsOnly.length === 10 && isValidIndianMobile(digitsOnly)) {
    return '+91' + digitsOnly;
  }

  // Case 3: More than 12 digits - try to extract last 10 digits
  if (digitsOnly.length > 12) {
    const last10 = digitsOnly.substring(digitsOnly.length - 10);
    if (isValidIndianMobile(last10)) {
      return '+91' + last10;
    }
  }

  // Case 4: Between 10-12 digits - try to extract valid 10 digits
  if (digitsOnly.length > 10 && digitsOnly.length < 12) {
    // Check if it starts with country code
    if (digitsOnly.startsWith('91')) {
      const mobileNumber = digitsOnly.substring(2);
      if (isValidIndianMobile(mobileNumber)) {
        return '+91' + mobileNumber;
      }
    }

    // Try last 10 digits
    const last10 = digitsOnly.substring(digitsOnly.length - 10);
    if (isValidIndianMobile(last10)) {
      return '+91' + last10;
    }
  }

  // If we can't normalize it, return null
  console.warn('Could not normalize phone number:', phoneNumber);
  return null;
};

/**
 * Validate if a string is a valid Indian mobile number (10 digits starting with 6-9)
 * @param {string} mobileNumber - 10-digit mobile number
 * @returns {boolean} - true if valid, false otherwise
 */
export const isValidIndianMobile = (mobileNumber) => {
  if (!mobileNumber || typeof mobileNumber !== 'string') {
    return false;
  }
  // Indian mobile numbers: 10 digits starting with 6, 7, 8, or 9
  return /^[6-9]\d{9}$/.test(mobileNumber);
};

/**
 * Format a phone number for display (+91 98765 43210)
 * @param {string} phoneNumber - Phone number in any format
 * @returns {string} - Formatted phone number for display
 */
export const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return '';
  }

  // First normalize the phone number
  const normalized = normalizePhoneNumber(phoneNumber);
  
  if (!normalized) {
    // If normalization fails, return the original or cleaned version
    return phoneNumber;
  }

  // Format as +91 98765 43210
  // normalized is in format: +919876543210
  const countryCode = normalized.substring(0, 3); // +91
  const firstPart = normalized.substring(3, 8);   // 98765
  const secondPart = normalized.substring(8);      // 43210

  return `${countryCode} ${firstPart} ${secondPart}`;
};

/**
 * Format a phone number for compact display (+91 9876543210)
 * @param {string} phoneNumber - Phone number in any format
 * @returns {string} - Formatted phone number for compact display
 */
export const formatPhoneCompact = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return '';
  }

  // First normalize the phone number
  const normalized = normalizePhoneNumber(phoneNumber);
  
  if (!normalized) {
    // If normalization fails, return the original or cleaned version
    return phoneNumber;
  }

  // Format as +91 9876543210
  const countryCode = normalized.substring(0, 3); // +91
  const number = normalized.substring(3);          // 9876543210

  return `${countryCode} ${number}`;
};

/**
 * Check if two phone numbers are equivalent
 * @param {string} phone1 - First phone number
 * @param {string} phone2 - Second phone number
 * @returns {boolean} - true if both numbers represent the same phone number
 */
export const arePhoneNumbersEquivalent = (phone1, phone2) => {
  const normalized1 = normalizePhoneNumber(phone1);
  const normalized2 = normalizePhoneNumber(phone2);

  if (!normalized1 || !normalized2) {
    return false;
  }

  return normalized1 === normalized2;
};

/**
 * Validate if a phone number is valid
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - true if valid and can be normalized
 */
export const isValidPhoneNumber = (phoneNumber) => {
  return normalizePhoneNumber(phoneNumber) !== null;
};
